import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, isEmpty } from 'lodash-es';
/* Models */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  modsRecordPath,
  MultiOfferModel,
  transformedQuestionsPath as multiOfferQuestionsV1Path,
  transformedQuestionsV2Path as multiOfferQuestionsPath,
} from 'client/data/models/multi-offer';
/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { logger } from 'client/utils/logger/logger';
/* Constants */
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';

import './vehicle-condition.scss';

export function VehicleConditionUI({ conditionsQuestions = {}, conditionsQuestionsV1 = {}, modsRecord = {} }) {
  const { conditionDetails } = modsRecord;
  const isV2 = !!conditionDetails?.v2;
  const activeQuestions = isV2 ? conditionsQuestions : conditionsQuestionsV1;
  const { questionsGroupedByCategory } = activeQuestions;

  useEffect(() => {
    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.WIDGET_VIEW, {
      creativeId: 'condition_questions_used_model_car_inventory_vin_detail',
    });
    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.DISPLAY_CONDITION_QUESTIONS);
  }, []);

  if (!(conditionDetails && questionsGroupedByCategory)) return null;

  let conditionAnswers = {};
  let questionsWithAnswers = [];

  try {
    if (isV2) {
      conditionAnswers = JSON.parse(get(conditionDetails, 'v2.rawData', '{}'));
    } else {
      questionsWithAnswers = [
        ...JSON.parse(get(conditionDetails, 'kmx.rawData', '[]')),
        ...JSON.parse(get(conditionDetails, 'carwiser.rawData', '[]')),
      ];
    }
  } catch (error) {
    logger.error(error, `Error while parsing JSON`);
    return null;
  }

  if (isV2 && isEmpty(conditionAnswers)) return null;

  const questionGroups = Object.keys(questionsGroupedByCategory);

  return (
    <div className="vehicle-condition-wrapper">
      {questionGroups.map((group, index) => (
        <Fragment key={group}>
          <div className="fw-bold text-cool-gray-10">{group}</div>
          <ul
            className={classNames(
              'question-group-wrapper list-unstyled',
              index !== questionGroups.length - 1 ? 'mb-1_5' : 'mb-0'
            )}
          >
            {questionsGroupedByCategory[group].map(({ id: currentId, desc, answers: questionAnswers }) => {
              let selectedAnswerDesc;
              if (isV2) {
                const selectedAnswerId = conditionAnswers[currentId]?.answer?.[0];
                selectedAnswerDesc = questionAnswers?.find(a => a.id === selectedAnswerId)?.desc;
              } else {
                const question = questionsWithAnswers.find(({ id }) => id === currentId?.toString());
                selectedAnswerDesc = question?.answers?.[0]?.desc;
              }

              return (
                <li key={currentId} className="py-0_5 d-flex justify-content-between align-items-center">
                  <div>{desc}</div>
                  <div className="fw-bold ms-1_5 answer text-end">{selectedAnswerDesc}</div>
                </li>
              );
            })}
          </ul>
        </Fragment>
      ))}
    </div>
  );
}

VehicleConditionUI.propTypes = {
  conditionsQuestions: PropTypes.shape({
    questionsUngrouped: PropTypes.arrayOf(PropTypes.shape({})),
    questionsFlatten: PropTypes.arrayOf(PropTypes.shape({})),
    questionsGroupedByCategory: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({}))),
  }),
  conditionsQuestionsV1: PropTypes.shape({
    questionsUngrouped: PropTypes.arrayOf(PropTypes.shape({})),
    questionsFlatten: PropTypes.arrayOf(PropTypes.shape({})),
    questionsGroupedByCategory: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({}))),
  }),
  modsRecord: PropTypes.shape({ conditionDetails: PropTypes.shape({}) }),
};

export const stateToPropsConfig = {
  conditionsQuestions: bindToPath(multiOfferQuestionsPath.build(), MultiOfferModel),
  conditionsQuestionsV1: bindToPath(multiOfferQuestionsV1Path.build(), MultiOfferModel),
  modsRecord: bindToPath(({ modsId }) => (modsId ? modsRecordPath.build(modsId) : null), MultiOfferModel),
};

export const VehicleCondition = connectToModel(VehicleConditionUI, stateToPropsConfig);
